import React, {HTMLProps, MouseEventHandler, useCallback, useEffect, useState} from 'react';
import cn from 'classnames';
import './CallRegion.sass';
import {TRACK_HEIGHT} from '@components/call-card/buildCommentBuckets';
import {Seconds} from '@store/@types/util';
import {registerEvents} from 'utils/registerEvents';
import ToastStore from '@store/toast/ToastStore';

interface CallRegionProps extends HTMLProps<HTMLDivElement> {
  startTime: Seconds;
  endTime: Seconds;
  duration: Seconds;
  type: 'comment' | 'section';
  mini?: boolean;
  bucketIndex?: number;
  className?: string;
  color?: string;
  setRef?: (ref: HTMLDivElement | null) => void;
  onResize?: (startTime: Seconds, endTime: Seconds) => Promise<any>;
  trackWidth?: number;
}

interface ResizeState {
  startPos: number;
  endPos: number;
  resizeSide: 'left' | 'right';
  isDragging: boolean;
}

export const CallRegion: React.FC<CallRegionProps> = ({
  children,
  color,
  className,
  endTime,
  startTime,
  type,
  mini,
  bucketIndex = 0,
  setRef,
  duration,
  onResize,
  trackWidth,
  ...rest
}) => {
  const [loading, setLoading] = useState(false);
  const [resizeState, setResizeState] = useState<ResizeState | null>(null);
  const isDragging = !!resizeState?.isDragging;

  const height = mini ? TRACK_HEIGHT.MINI : TRACK_HEIGHT.DEFAULT;
  const {left, width} = getDimensions(startTime, endTime, duration, resizeState, trackWidth);
  const style = {
    left: left * 100 + '%',
    width: width * 100 + '%',
    backgroundColor: color,
    marginTop: bucketIndex * height,
    height,
  };

  useEffect(() => {
    if (!isDragging) return;
    return registerEvents(document, [
      ['mousemove', e => setResizeState(s => s && {...s, endPos: e.clientX})],
      ['mouseup', () => setResizeState(s => s && {...s, isDragging: false})],
    ]);
  }, [setResizeState, isDragging]); // Few dependencies as possible to prevent register event spam

  const saveHighlight = useCallback(() => {
    if (loading || !onResize) return;
    setLoading(true);
    const startTime = left * duration;
    const endTime = (left + width) * duration;
    onResize(startTime, endTime)
      .catch(() => {
        ToastStore.addToast({
          message: 'Failed to update comment.',
          type: 'error',
        });
      })
      .finally(() => {
        setResizeState(null);
        setLoading(false);
      });
  }, [left, duration, width, onResize, setResizeState, loading, setLoading]);

  useEffect(() => {
    if (!isDragging && resizeState) {
      saveHighlight();
    }
  }, [resizeState, isDragging, saveHighlight]);

  const mouseDownHandler =
    (resizeSide: 'left' | 'right'): MouseEventHandler | undefined =>
    e =>
      setResizeState({resizeSide, startPos: e.clientX, endPos: e.clientX, isDragging: true});

  return (
    <div
      {...rest}
      ref={r => setRef?.(r)}
      style={style}
      className={cn('call-region', className, type)}>
      {children}
      {onResize && (
        <>
          <div onMouseDown={mouseDownHandler('left')} className="anchor left" />
          <div onMouseDown={mouseDownHandler('right')} className="anchor right" />
        </>
      )}
    </div>
  );
};

interface Dimensions {
  left: number;
  width: number;
}

// Prevent drag resizing from creating 0 width highlights. Minimum is 1% of duration
const MIN_BUFFER = 0.01;

// Get the size of the comment given changes from click/drag resize
function getDimensions(
  startTime: Seconds,
  endTime: Seconds,
  duration: Seconds,
  resizeState: ResizeState | null,
  trackWidth: number | undefined
): Dimensions {
  const startPct = startTime / duration;
  const endPct = endTime / duration;

  // We dont have any drag changes, or the track ref isn't loaded yet
  if (!resizeState || !trackWidth) {
    return {
      left: startPct,
      width: endPct - startPct,
    };
  }

  const {startPos, endPos, resizeSide} = resizeState;
  const pctChange = (endPos - startPos) / trackWidth;

  // Apply pct change to left or right side
  if (resizeSide === 'left') {
    const newStart = Math.min(startPct + pctChange, endPct - MIN_BUFFER);
    return {left: newStart, width: endPct - newStart};
  } else {
    const newEnd = Math.max(endPct + pctChange, startPct + MIN_BUFFER);
    return {left: startPct, width: newEnd - startPct};
  }
}
